import {Subject} from "rxjs";
import React, {useCallback} from "react";
import {ReportModal} from "../form-components/ReportModal";
import {useObservable} from "../../../rxsupport";
import {StaffingAction} from "./domain";
import {Controller, FormProvider, useForm, useFormContext} from "react-hook-form";
import {SimpleDropdown} from "../../../common";
import {FatOutlineButton} from "../../../common/component/FatOutlineButton";

type ActionForm = {
    action: StaffingAction;
};

const STAFFING_ACTIONS: StaffingAction[] = [
    'Staffing Plan',
    'Change Hours Addendum',
    'ABA Addendum Report',
];

function ActionSingleSelect() {
    const {
        setValue,
        clearErrors,
        control,
        formState: {
            errors
        },
    } = useFormContext<ActionForm>();
    return (
        <div className="text-black-200 pb-2 text-base" style={{minHeight: '200px'}}>
            <div className="flex">
                <div className="flex items-center">
                    Action
                </div>
                <div className="ml-4 items-center">
                    <Controller
                        name="action"
                        control={control}
                        rules={{
                            required: true
                        }}
                        render={({field: {value}}) =>
                            <SimpleDropdown
                                options={STAFFING_ACTIONS}
                                selected={value}
                                hasErrorBorder={errors.action !== undefined}
                                setSelected={(selected) => {
                                    setValue("action", selected as StaffingAction);
                                    clearErrors("action");
                                }}
                                textColor={"black-200"}
                            />
                        }/>
                </div>
            </div>

            {
                errors.action &&
                <div className="text-red-700">
                    Please select “Staffing Action” before continuing
                </div>
            }
        </div>
    );
}

type Props = {
    visible$: Subject<boolean>;
    onFinished: (action: StaffingAction) => Promise<void>;
};

export function StaffingPlanActionModal({visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<ActionForm>();

    const close = useCallback(() => {
        visible$.next(false);
        methods.reset();
    }, [visible$, methods]);

    const submit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        methods.handleSubmit(async (data: ActionForm) => {
            await onFinished(data.action)
            close();
        })(e);
    }, [methods, onFinished, close]);

    return (
        <ReportModal visible={visible} title={'Staffing Action'} onClose={close}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    <div className="space-y-6">
                        <div className="flex flex-col w-full p-4 scrollbar-always"
                             style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>

                            <p className='py-2'>
                                To address recurring staffing issues you can submit a "Staffing Plan" or an "Addendum Report".
                                Please indicate how staffing will be addressed:
                            </p>

                            <div style={{marginLeft: '100px'}}>
                                <ActionSingleSelect/>
                            </div>
                        </div>
                    </div>

                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                        <div className="flex w-1/4">
                            <FatOutlineButton color={'red'} text={'Cancel'} onClick={close}/>
                            <span style={{width: 10}}/>
                            <FatOutlineButton type="submit" text='Continue' color={"green"}/>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </ReportModal>
    );
}
